
import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { Tax } from '@shared/types';
import { toast } from 'sonner';
import { useUser } from './UserContext';

interface TaxContextType {
  taxes: Tax[];
  getTaxById: (id: string) => Tax | undefined;
  isLoading: boolean;
  refreshTaxes: () => Promise<void>;
}

const TaxContext = createContext<TaxContextType | undefined>(undefined);

export const useTaxes = () => {
  const context = useContext(TaxContext);
  if (!context) {
    throw new Error('useTaxes must be used within a TaxProvider');
  }
  return context;
};

export const TaxProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { currentUser, fetchWithAuth } = useUser();
  const [taxes, setTaxes] = useState<Tax[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const loadTaxes = useCallback(async () => {
    if (!currentUser) {
      setTaxes([]);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchWithAuth('/impuestos');
      if (!response.ok) {
        throw new Error('Failed to fetch taxes');
      }
      const data = await response.json();
      setTaxes(data);
    } catch (error) {
      console.error('Error loading taxes:', error);
      toast.error('Error al cargar impuestos', {
        description: error instanceof Error ? error.message : 'Error desconocido'
      });
      setTaxes([]);
    } finally {
      setIsLoading(false);
    }
  }, [currentUser, fetchWithAuth]);

  useEffect(() => {
    if (currentUser) {
      loadTaxes();
    } else {
      setTaxes([]);
      setIsLoading(false);
    }
  }, [currentUser, loadTaxes]);

  // Memoize getTaxById to prevent unnecessary re-renders
  const getTaxById = useCallback((id: string) => taxes.find(t => t.id === id), [taxes]);

  const value = useMemo(
    () => ({ taxes, getTaxById, isLoading, refreshTaxes: loadTaxes }),
    [taxes, getTaxById, isLoading, loadTaxes]
  );

  return (
    <TaxContext.Provider value={value}>
      {children}
    </TaxContext.Provider>
  );
};
